import Parse from "parse";

const VAPID_PUBLIC_KEY = process.env.VUE_APP_VAPID_PUBLIC_KEY;

async function registerSubscriptionWithServer(sub) {
  const json = sub.toJSON();
  await Parse.Cloud.run("registerPushDevice", {
    endpoint:    json.endpoint,
    keys_p256dh: json.keys.p256dh,
    keys_auth:   json.keys.auth,
    userAgent:   navigator.userAgent,
  });
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64  = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw     = atob(base64);
  const out     = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

export async function getCurrentSubscription() {
  if (!isPushSupported()) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

export async function subscribePushDevice() {
  if (!isPushSupported()) {
    throw new Error("Push not supported on this browser.");
  }
  if (!VAPID_PUBLIC_KEY) {
    throw new Error("VAPID public key not configured.");
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Notification permission denied.");
  }

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }

  await registerSubscriptionWithServer(sub);
  return true;
}

// Returns true if push is wired end-to-end on this device right now:
// pref enabled, OS permission granted, AND a live PushManager subscription
// exists. Used to render the toggle truthfully — `notificationPrefs.enabled`
// alone is not enough because it defaults to `true` server-side at signup,
// before any of the browser-side wiring has happened.
export async function isPushActive() {
  if (!isPushSupported()) return false;
  if (typeof Notification === "undefined") return false;
  if (Notification.permission !== "granted") return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return sub != null;
  } catch {
    return false;
  }
}

// Silent reconcile: on app boot, repair lost subscriptions without prompting
// the user. Two failure modes this addresses:
//   1. Server-side 404/410 cleanup destroyed the PushDevice row but the
//      browser still has a live subscription. Re-register is idempotent on
//      `endpoint`, so calling it costs one cloud round-trip and restores the
//      row.
//   2. The browser dropped the subscription (SW update, push-service rotation,
//      data clear). Browser permission is still "granted" — we can call
//      `pushManager.subscribe()` again silently and re-register.
// No-op when the user has explicitly disabled, when permission isn't granted
// (we never silently prompt), or when push isn't supported. Never throws.
export async function reconcilePushSubscription() {
  if (!isPushSupported()) return false;
  if (!VAPID_PUBLIC_KEY) return false;
  if (typeof Notification === "undefined") return false;
  if (Notification.permission !== "granted") return false;

  const user = Parse.User.current();
  if (!user) return false;
  const prefs = user.get("notificationPrefs");
  const wantEnabled =
    !prefs || typeof prefs.enabled !== "boolean" || prefs.enabled !== false;
  if (!wantEnabled) return false;

  try {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }
    await registerSubscriptionWithServer(sub);
    return true;
  } catch {
    return false;
  }
}

// Workaround for the Chromium-on-Android URL chip in OS notifications.
// A push subscription created from a regular browser tab is attributed to
// the site origin, so the lock-screen banner shows "www.scrawl.ink" next
// to the title even after the user installs the PWA. Subscriptions created
// from inside the standalone PWA are attributed to the installed app and
// drop the URL chip. On first boot from the installed PWA we silently
// unsubscribe the browser-bound subscription and re-subscribe from the
// PWA context, then mark it done so it never runs again on this device.
const PWA_REBIND_FLAG = "scrawl:push-pwa-rebound-v1";

function isStandalonePWA() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  if (typeof navigator !== "undefined" && navigator.standalone === true) return true;
  return false;
}

export async function rebindPushIfPWAContextChanged() {
  if (!isPushSupported()) return false;
  if (!VAPID_PUBLIC_KEY) return false;
  if (!isStandalonePWA()) return false;
  if (typeof Notification === "undefined") return false;
  if (Notification.permission !== "granted") return false;

  try {
    if (localStorage.getItem(PWA_REBIND_FLAG) === "1") return false;
  } catch {
    return false;
  }

  try {
    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    if (!existing) {
      try { localStorage.setItem(PWA_REBIND_FLAG, "1"); } catch { /* ignore */ }
      return false;
    }

    // Best-effort cleanup of the old PushDevice row. If this fails the row
    // is harmlessly stale — the next push attempt to the dead endpoint
    // will 404/410 and `webpush.sendPushToUser` already prunes those.
    try {
      await Parse.Cloud.run("unregisterPushDevice", { endpoint: existing.endpoint });
    } catch { /* ignore */ }

    await existing.unsubscribe();

    const fresh = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });

    await registerSubscriptionWithServer(fresh);

    try { localStorage.setItem(PWA_REBIND_FLAG, "1"); } catch { /* ignore */ }
    return true;
  } catch {
    // Leave the flag unset so we retry on next boot. Never throw — a push
    // hiccup must not block app startup.
    return false;
  }
}

export async function unsubscribePushDevice() {
  if (!isPushSupported()) return false;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return true;

  try {
    await Parse.Cloud.run("unregisterPushDevice", { endpoint: sub.endpoint });
  } catch (e) {
    console.error("unregisterPushDevice cloud call failed", e);
  }

  await sub.unsubscribe();
  return true;
}
